import { AnimatePresence, motion } from "framer-motion";
import AutoAwesomeRounded from "@mui/icons-material/AutoAwesomeRounded";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import MonitorHeartRounded from "@mui/icons-material/MonitorHeartRounded";
import ShowChartRounded from "@mui/icons-material/ShowChartRounded";
import SpaceDashboardRounded from "@mui/icons-material/SpaceDashboardRounded";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AIInsights from "./components/AIInsights";
import AISuggestions from "./components/AISuggestions";
import CompareCharts from "./components/CompareCharts";
import HealthTrends from "./components/HealthTrends";
import HeroSection from "./components/HeroSection";
import ReportsTable from "./components/ReportsTable";
import SummaryCards from "./components/SummaryCards";
import { DUMMY_REPORTS, SUMMARY_STATS } from "./data";

const TABS = [
  { id: "reports", label: "Reports", Icon: DescriptionRounded },
  { id: "compare", label: "Compare", Icon: BarChartRounded },
  { id: "trends", label: "Trends", Icon: ShowChartRounded },
  { id: "ai", label: "AI insights", Icon: AutoAwesomeRounded },
  { id: "suggestions", label: "Suggestions", Icon: MonitorHeartRounded }
];

function PageSkeleton({ embedded }) {
  const wrap = embedded ? "w-full" : "min-h-screen bg-slate-50";
  return (
    <div className={wrap}>
      <div className={`mx-auto max-w-7xl space-y-4 px-0 py-2 sm:py-4 ${embedded ? "" : "px-4 sm:px-6 lg:px-8"}`}>
        <div className="h-36 animate-pulse rounded-2xl bg-slate-200/70 sm:h-40" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-200/60" />
          ))}
        </div>
        <div className="h-12 animate-pulse rounded-xl bg-slate-200/60" />
        <div className="h-[420px] animate-pulse rounded-2xl bg-slate-200/50" />
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {boolean} [props.embedded=false] When true, hides standalone chrome (top bar, full-page bg) for use inside DashboardLayout.
 * @param {Array} [props.summaryItems] Override summary cards (e.g. from live profile data).
 * @param {string} [props.heroGreeting] Optional line above the hero title (e.g. user first name).
 * @param {Array<{ id: string, date: string, testName: string, category: string, keyResult: string, status: string, doctor: string }> | undefined} props.reports
 * @param {boolean} [props.useDemoData=true]
 * @param {boolean} [props.reportsLoading=false]
 * @param {number} [props.initialDelayMs=800] Set to 0 when embedding with parent-controlled fetch.
 */
export default function MediTrackDashboard({
  embedded = false,
  summaryItems: summaryItemsProp,
  heroGreeting,
  reports: reportsProp,
  useDemoData = true,
  reportsLoading = false,
  initialDelayMs = 800
}) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("reports");
  const [bootstrapping, setBootstrapping] = useState(!embedded && initialDelayMs > 0);

  useEffect(() => {
    if (embedded || initialDelayMs <= 0) {
      setBootstrapping(false);
      return;
    }
    const t = window.setTimeout(() => setBootstrapping(false), initialDelayMs);
    return () => window.clearTimeout(t);
  }, [embedded, initialDelayMs]);

  const reports = useMemo(() => {
    if (Array.isArray(reportsProp)) return reportsProp;
    return useDemoData ? DUMMY_REPORTS : [];
  }, [reportsProp, useDemoData]);

  const summaryItems = useMemo(() => {
    if (summaryItemsProp && summaryItemsProp.length) return summaryItemsProp;
    return SUMMARY_STATS;
  }, [summaryItemsProp]);

  const handleDownloadAll = useCallback(() => {
    console.info("Download all reports (wire to API / zip)");
  }, []);

  const handleAddReport = useCallback(() => {
    navigate("/upload");
  }, [navigate]);

  const handleDownloadRow = useCallback((row) => {
    console.info("Download report", row.id);
  }, []);

  if (bootstrapping) {
    return <PageSkeleton embedded={embedded} />;
  }

  const outer = embedded ? "w-full" : "min-h-screen bg-slate-50";
  const mainPad = embedded
    ? "w-full min-w-0 space-y-4 pb-4 md:space-y-6"
    : "mx-auto min-w-0 max-w-7xl space-y-4 px-4 py-4 sm:space-y-5 sm:px-5 sm:py-6 md:space-y-6 lg:px-8 lg:py-8";

  return (
    <div className={outer}>
      {!embedded && (
        <div className="border-b border-slate-200/80 bg-white/80 backdrop-blur-md">
          <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white">
              <SpaceDashboardRounded sx={{ fontSize: 20 }} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Dashboard</p>
              <p className="text-sm font-semibold text-slate-900">MediTrack</p>
            </div>
          </div>
        </div>
      )}

      <main className={mainPad}>
        {heroGreeting ? (
          <p className="text-sm font-medium text-slate-600">
            <span className="text-slate-900">{heroGreeting}</span>
          </p>
        ) : null}

        <HeroSection onDownloadAll={handleDownloadAll} onAddReport={handleAddReport} />
        <SummaryCards items={summaryItems} />

        <div className="rounded-2xl border border-slate-200/90 bg-white p-1.5 shadow-sm sm:p-2">
          <div className="flex gap-1 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {TABS.map(({ id, label, Icon }) => {
              const active = activeTab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`inline-flex min-h-10 shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition-all duration-200 sm:text-sm ${
                    active
                      ? "bg-slate-900 text-white shadow-md shadow-slate-900/15 [&_.MuiSvgIcon-root]:text-white"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <Icon sx={{ fontSize: 20 }} className="opacity-90" />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            className="min-w-0"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            {activeTab === "reports" && (
              <ReportsTable reports={reports} loading={reportsLoading} onDownloadRow={handleDownloadRow} />
            )}
            {activeTab === "compare" && <CompareCharts reports={reports} useDemoData={useDemoData} />}
            {activeTab === "trends" && <HealthTrends reports={reports} useDemoData={useDemoData} />}
            {activeTab === "ai" && <AIInsights reports={reports} />}
            {activeTab === "suggestions" && (
              <AISuggestions
                onDownloadSummary={() => console.info("Download summary PDF")}
                onDownloadPack={() => console.info("Download full pack")}
                onShare={() => console.info("Share with doctor")}
              />
            )}
          </motion.div>
        </AnimatePresence>

        <footer className="flex flex-col gap-4 rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm sm:p-5 md:flex-row md:items-center md:justify-between md:p-6">
          <div>
            <p className="text-sm font-semibold text-slate-900">MediTrack — your health, simplified</p>
            <p className="mt-1 max-w-xl text-xs leading-relaxed text-slate-500">
              For personal tracking only. Always consult a qualified professional for diagnosis and treatment decisions.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-800 ring-1 ring-sky-600/15">
              AI-assisted
            </span>
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-600/15">
              Encrypted in transit
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-500/15">
              Privacy-first
            </span>
          </div>
        </footer>
      </main>
    </div>
  );
}
